// Copyright (c) 2024, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  LightModeOutlined,
  DarkModeRounded,
  HelpOutlineSharp,
} from "@mui/icons-material";
import LogoLight from "../assets/images/wso2_logo_white.svg";
import LogoDark from "../assets/images/wso2_logo_black.svg";
import { ThemeMode } from "../styles/theme";
import { useDispatch } from "react-redux";
import { setDarkTheme } from "../store/themeSlice";
import { FRONTEND_BASE_URL } from "../utils/constants";

const THEME_MODE = "themeMode";

const NavBar: React.FC<{
  appName: string;
  onThemeChange: (theme: boolean) => void;
}> = ({ appName, onThemeChange }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const localTheme = localStorage.getItem(THEME_MODE);
    if (localTheme === null) {
      localStorage.setItem(THEME_MODE, ThemeMode.Dark);
    }
    if (localTheme === ThemeMode.Dark) {
      setDarkMode(true);
    }
  }, []);

  const handleThemeChange = () => {
    setDarkMode(!darkMode);
    localStorage.setItem(
      THEME_MODE,
      !darkMode ? ThemeMode.Dark : ThemeMode.Light
    );
  };

  useEffect(() => {
    onThemeChange(darkMode);
    dispatch(setDarkTheme(darkMode));
  }, [darkMode]);

  const isMobileScreen = useMediaQuery("(max-width:37.5rem)");

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: theme.palette.background.paper,
        backgroundImage: "none",
      }}
    >
      <Toolbar>
        <a href={FRONTEND_BASE_URL} style={{ display: "flex", alignItems: "center" }}>
          <img
            src={darkMode ? LogoLight : LogoDark}
            style={{
              width: "6rem",
              height: "auto",
              marginRight: "1rem",
            }}
            alt="Logo"
          />
        </a>
        <a
          href={FRONTEND_BASE_URL}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <Typography
            variant={isMobileScreen ? "body1" : "h6"}
            sx={{
              pl: 2.5,
              borderLeft: "0.15rem solid",
              fontWeight: 600,
              borderColor: theme.palette.text.secondary,
              color: theme.palette.text.primary,
            }}
          >
            {appName}
          </Typography>
        </a>
        <div style={{ flexGrow: 1 }}></div>

        <Tooltip title="Get Help!">
          <IconButton
            sx={{ color: theme.palette.text.primary, mr: { md: 0.5, xs: 0 } }}
            href={`mailto:${window.config.SUPPORT_EMAIL_ADDRESS}`}
          >
            <HelpOutlineSharp />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
        >
          <IconButton
            onClick={handleThemeChange}
            sx={{ color: theme.palette.text.primary }}
          >
            {darkMode ? <DarkModeRounded /> : <LightModeOutlined />}
          </IconButton>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
};

export default NavBar;
